// src/features/booking/ResourceCard.tsx
// Catalog card: resource glance — image, name, price, rating, status, capacity.

import { Link } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { BookableResource } from '@core/types/booking';
import { fmtMoney, RESOURCE_STATUS_KEYS, RESOURCE_STATUS_VARIANT } from './lib';

export function ResourceCard({ resource }: { resource: BookableResource }) {
  const { t, locale } = useI18n();
  const id = resource.entity?.id ?? '';
  const status = resource.status ?? 'BOOKABLE_RESOURCE_STATUS_ACTIVE';
  const statusKey = RESOURCE_STATUS_KEYS[status] ?? 'active';

  return (
    <Link
      to={`/booking/resources/${id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary"
    >
      <div className="aspect-[16/9] w-full shrink-0 overflow-hidden bg-muted">
        {resource.cover_image_url ? (
          <img
            src={resource.cover_image_url}
            alt=""
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl text-muted-foreground" aria-hidden="true">
            {resource.icon || '◻'}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="truncate text-sm font-medium text-foreground">{resource.name}</h3>
          {status !== 'BOOKABLE_RESOURCE_STATUS_ACTIVE' && (
            <Badge variant={RESOURCE_STATUS_VARIANT[status] ?? 'default'} size="sm">
              {t(`booking.resource.status.${statusKey}`)}
            </Badge>
          )}
        </div>
        {resource.short_description && (
          <p className="line-clamp-2 text-xs text-muted-foreground">{resource.short_description}</p>
        )}
        <div className="mt-auto flex items-center justify-between pt-1.5 text-xs text-muted-foreground">
          <span>
            {resource.pricing?.base_price_cents !== undefined
              ? fmtMoney(resource.pricing.base_price_cents, resource.pricing.currency, locale)
              : t('booking.resource.priceOnRequest')}
          </span>
          {(resource.average_rating ?? 0) > 0 && (
            <span className="flex items-center gap-1">
              <span aria-hidden="true">★</span>
              {resource.average_rating?.toFixed(1)}
              <span>({resource.total_reviews ?? 0})</span>
            </span>
          )}
        </div>
        {resource.categories && resource.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {resource.categories.slice(0, 3).map((c) => (
              <span key={c} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
